import { useQuery } from "@tanstack/react-query";

import { listAllAssignedTasks, type PaginatedTasks } from "@/api/tasks";
import { TASKS_KEY } from "@/hooks/useTasks";
import { useAppSelector } from "@/stores/hooks";
import { hasPendingTask } from "@/utils/task-pending";

// POC parity: the header polls the task list on an interval so a newly created
// task lights the red dot without a manual refresh. 60s matches the POC
// `setInterval` cadence; 30s staleTime dedupes with the TasksModal observer.
const POLL_MS = 60_000;
const STALE_MS = 30_000;
// Filter to NOT-yet-quoted tasks (`quoted=false` → svc-06 `quoted_at IS NULL`).
// We must NOT filter by `status:"open"`: tasks live at `status="draft"` for
// their whole MVP lifecycle (open/quoted is derived from `quoted_at`, not the
// `status` column — see svc-06 snapshot.repository.ts), so `status:"open"`
// matched ZERO rows and the red dot never lit even with pending work.
const BADGE_ASSIGNED_QUERY = {
  quoted: "false",
} as const;

/**
 * Drives the NavBar "new task" red dot (issue: MVP didn't show it like POC).
 *
 * STAFF-ONLY signal. The dot means "there is pending work in the task/quote/
 * approval flow" — so it is shown ONLY to roles that act on that flow: internal
 * staff (`canWorkForOthers` or admin, i.e. admin / sales / engineering), who
 * watch their responsibility-assigned OPEN queue
 * (`GET /v1/tasks?assigned=true&quoted=false`). External
 * customers and distributors submit an RFQ and then wait; they are NOT part of
 * the flow, so they never see the dot (it would be persistent noise until staff
 * quote their request).
 *
 * The query key shares the `["tasks","assigned", …]` prefix used by `useTasks`, so
 * `useCreateTask`'s invalidation refetches the badge even when the TasksModal is
 * closed. Semantics match the POC: the dot reflects pending work and is NOT
 * cleared merely by opening the menu — it clears when nothing is pending.
 */
export function useTaskNotificationBadge(): { hasNotification: boolean } {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const user = useAppSelector((s) => s.auth.user);

  const isInternalStaff = !!user?.role?.canWorkForOthers;
  const enabled = isAuthenticated && !!user && isInternalStaff;

  const queue = useQuery<PaginatedTasks>({
    queryKey: [...TASKS_KEY, "assigned", BADGE_ASSIGNED_QUERY],
    // Scan every server page: a still-pending task may be older than the first
    // page, and the dot must agree with the full assigned inbox.
    queryFn: () => listAllAssignedTasks(BADGE_ASSIGNED_QUERY),
    enabled,
    staleTime: STALE_MS,
    refetchInterval: POLL_MS,
    retry: false,
  });

  return { hasNotification: enabled && hasPendingTask(queue.data?.items) };
}
